// BenchmarkPanel.jsx — D11 Sprint 2.5 (2026-04-30)
// Aba dedicada "Benchmark" no CompanyProfileScreen.
// Dropdowns manuais pra CNAE Section + UF + Modalidade.
// Layout rico: chart 12m+forecast com bands, score, comparação peer,
// national trend, diagnostics modelo.

import React, { useEffect, useState, useMemo } from "react";

const PRS_BASE = (typeof window !== "undefined" && window.__PRS_BASE__) || "https://api.financialmind.ai";

const C = {
  azure: "#1a3f6f",
  green: "#0f7a3f",
  greenLight: "#22C55E",
  red: "#dc2626",
  amber: "#f59e0b",
  gray: "#6b7280",
  text: "#1f2937",
  dim: "#9ca3af",
  muted: "#6b7280",
  border: "#e5e7eb",
  bg: "#f9fafb",
  card: "#ffffff",
};

// CNAE Sections (A-U) — IBGE CBOC 2.3
const SECTIONS = [
  { letter: "A", code: "SA", label: "Agricultura, pecuária, pesca e aquicultura", range: "01-03" },
  { letter: "B", code: "SB", label: "Indústrias extrativas",                   range: "05-09" },
  { letter: "C", code: "SC", label: "Indústrias de transformação",             range: "10-33" },
  { letter: "D", code: "SD", label: "Eletricidade e gás",                      range: "35"    },
  { letter: "E", code: "SE", label: "Água, esgoto, gestão de resíduos",        range: "36-39" },
  { letter: "F", code: "SF", label: "Construção",                              range: "41-43" },
  { letter: "G", code: "SG", label: "Comércio; reparação de veículos",         range: "45-47" },
  { letter: "H", code: "SH", label: "Transporte, armazenagem e correio",       range: "49-53" },
  { letter: "I", code: "SI", label: "Alojamento e alimentação",                range: "55-56" },
  { letter: "J", code: "SJ", label: "Informação e comunicação",                range: "58-63" },
  { letter: "K", code: "SK", label: "Atividades financeiras, seguros",         range: "64-66" },
  { letter: "L", code: "SL", label: "Atividades imobiliárias",                 range: "68"    },
  { letter: "M", code: "SM", label: "Profissionais, científicas, técnicas",    range: "69-75" },
  { letter: "N", code: "SN", label: "Administrativas e complementares",        range: "77-82" },
  { letter: "O", code: "SO", label: "Administração pública, defesa",           range: "84"    },
  { letter: "P", code: "SP", label: "Educação",                                range: "85"    },
  { letter: "Q", code: "SQ", label: "Saúde humana e serviços sociais",         range: "86-88" },
  { letter: "R", code: "SR", label: "Artes, cultura, esporte",                 range: "90-93" },
  { letter: "S", code: "SS", label: "Outras atividades de serviços",           range: "94-96" },
  { letter: "T", code: "ST", label: "Serviços domésticos",                     range: "97"    },
  { letter: "U", code: "SU", label: "Organismos internacionais",               range: "99"    },
];

const UFS = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
  "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO",
];

const MODALIDADES = [
  "Empréstimos",
  "Financiamentos",
  "Financiamentos imobiliários",
  "Financiamentos rurais  (ex-financiamentos rurais e agroindustriais)",
  "Financiamentos à exportação",
  "Financiamentos à importação",
  "Financiamentos de infraestrutura e desenvolvimento",
  "Operações de arrendamento",
  "Direitos creditórios descontados",
  "Outros créditos",
  "Adiantamentos a depositantes",
];

// Auto-detect Section letter de um CNAE 7-digit
function cnaeToSection(cnae) {
  if (!cnae) return null;
  const c = parseInt(String(cnae).replace(/\D/g, "").substring(0, 2), 10);
  if (isNaN(c)) return null;
  if (c <= 3) return "A"; if (c <= 9) return "B"; if (c <= 33) return "C";
  if (c === 35) return "D"; if (c <= 39) return "E"; if (c <= 43) return "F";
  if (c <= 47) return "G"; if (c <= 53) return "H"; if (c <= 56) return "I";
  if (c <= 63) return "J"; if (c <= 66) return "K"; if (c === 68) return "L";
  if (c <= 75) return "M"; if (c <= 82) return "N"; if (c === 84) return "O";
  if (c === 85) return "P"; if (c <= 88) return "Q"; if (c <= 93) return "R";
  if (c <= 96) return "S"; if (c === 97) return "T"; if (c === 99) return "U";
  return null;
}

function fPct(v, dec = 2) {
  if (v == null || isNaN(v)) return "—";
  return (v * 100).toFixed(dec) + "%";
}

function alignColor(align) {
  return ({
    muito_melhor_que_setor: "#15803D",
    melhor_que_setor:       "#22C55E",
    alinhado_com_setor:     "#6b7280",
    pior_que_setor:         "#f59e0b",
    muito_pior_que_setor:   "#dc2626",
  })[align] || C.dim;
}

function alignLabel(align) {
  return ({
    muito_melhor_que_setor: "MUITO MELHOR QUE SETOR",
    melhor_que_setor:       "MELHOR QUE SETOR",
    alinhado_com_setor:     "ALINHADO COM SETOR",
    pior_que_setor:         "PIOR QUE SETOR",
    muito_pior_que_setor:   "MUITO PIOR QUE SETOR",
  })[align] || "—";
}

// Big chart: tendência 12m + forecast 6m+12m com bands
function ChartTendenciaForecast({ trend, fc6, fc12, currentInad, w = 720, h = 280 }) {
  const PAD = { t: 24, r: 24, b: 38, l: 56 };
  const cW = w - PAD.l - PAD.r, cH = h - PAD.t - PAD.b;

  // Combine trend + forecast points
  const trendPts = (trend || []).map(d => ({ data: d.data, val: d.inadimplencia, kind: "hist" }));
  const lastDate = trendPts.length ? new Date(trendPts[trendPts.length - 1].data) : new Date();
  const fc6Date = new Date(lastDate); fc6Date.setMonth(fc6Date.getMonth() + 6);
  const fc12Date = new Date(lastDate); fc12Date.setMonth(fc12Date.getMonth() + 12);

  const allPts = [...trendPts];
  if (fc6) allPts.push({ data: fc6Date.toISOString().slice(0, 10), val: fc6.point, kind: "fc6", ic80: fc6.ic_80, ic95: fc6.ic_95 });
  if (fc12) allPts.push({ data: fc12Date.toISOString().slice(0, 10), val: fc12.point, kind: "fc12", ic80: fc12.ic_80, ic95: fc12.ic_95 });

  if (allPts.length < 2) return <div style={{ padding: 40, textAlign: "center", color: C.dim }}>Dados insuficientes pra renderizar gráfico</div>;

  const allVals = [
    ...allPts.map(p => p.val),
    ...allPts.filter(p => p.ic95).flatMap(p => p.ic95),
    ...allPts.filter(p => p.ic80).flatMap(p => p.ic80),
  ].filter(v => v != null);

  const minV = 0;
  const maxV = Math.max(...allVals) * 1.1;

  const xOf = i => PAD.l + (i / (allPts.length - 1)) * cW;
  const yOf = v => PAD.t + cH - ((v - minV) / (maxV - minV)) * cH;

  const histPts = allPts.filter(p => p.kind === "hist");
  const fcPts = allPts.filter(p => p.kind !== "hist");

  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      {/* Grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
        const v = minV + p * (maxV - minV);
        return (
          <g key={i}>
            <line x1={PAD.l} y1={yOf(v)} x2={PAD.l + cW} y2={yOf(v)} stroke={C.border} strokeDasharray="2,2" />
            <text x={PAD.l - 8} y={yOf(v) + 4} fontSize="10" fill={C.dim} textAnchor="end">{(v * 100).toFixed(1)}%</text>
          </g>
        );
      })}

      {/* IC 95% band (fc only) */}
      {fcPts.length > 0 && fcPts[0].ic95 && (() => {
        const lastHistIdx = histPts.length - 1;
        const points95 = [];
        // upper edge from last hist to fc12
        if (histPts.length > 0) points95.push({ x: xOf(lastHistIdx), y: yOf(histPts[lastHistIdx].val) });
        fcPts.forEach((p, i) => {
          points95.push({ x: xOf(histPts.length + i), y: yOf(p.ic95[1]) });
        });
        // lower edge back
        for (let i = fcPts.length - 1; i >= 0; i--) {
          points95.push({ x: xOf(histPts.length + i), y: yOf(fcPts[i].ic95[0]) });
        }
        if (histPts.length > 0) points95.push({ x: xOf(lastHistIdx), y: yOf(histPts[lastHistIdx].val) });
        return <polygon points={points95.map(p => `${p.x},${p.y}`).join(" ")} fill={C.azure} fillOpacity="0.08" />;
      })()}

      {/* IC 80% band (fc only) */}
      {fcPts.length > 0 && fcPts[0].ic80 && (() => {
        const lastHistIdx = histPts.length - 1;
        const points80 = [];
        if (histPts.length > 0) points80.push({ x: xOf(lastHistIdx), y: yOf(histPts[lastHistIdx].val) });
        fcPts.forEach((p, i) => {
          points80.push({ x: xOf(histPts.length + i), y: yOf(p.ic80[1]) });
        });
        for (let i = fcPts.length - 1; i >= 0; i--) {
          points80.push({ x: xOf(histPts.length + i), y: yOf(fcPts[i].ic80[0]) });
        }
        if (histPts.length > 0) points80.push({ x: xOf(lastHistIdx), y: yOf(histPts[lastHistIdx].val) });
        return <polygon points={points80.map(p => `${p.x},${p.y}`).join(" ")} fill={C.azure} fillOpacity="0.20" />;
      })()}

      {/* Hist line */}
      {histPts.length >= 2 && (
        <polyline
          points={histPts.map((p, i) => `${xOf(i)},${yOf(p.val)}`).join(" ")}
          fill="none" stroke={C.text} strokeWidth="2"
        />
      )}

      {/* Forecast dashed line */}
      {fcPts.length > 0 && histPts.length > 0 && (
        <polyline
          points={[histPts[histPts.length-1], ...fcPts].map((p, i) => {
            const idx = i === 0 ? histPts.length - 1 : histPts.length + i - 1;
            return `${xOf(idx)},${yOf(p.val)}`;
          }).join(" ")}
          fill="none" stroke={C.azure} strokeWidth="2" strokeDasharray="6,3"
        />
      )}

      {/* Hist dots */}
      {histPts.map((p, i) => (
        <circle key={`h${i}`} cx={xOf(i)} cy={yOf(p.val)} r="3" fill={C.text} />
      ))}

      {/* FC dots + labels */}
      {fcPts.map((p, i) => {
        const idx = histPts.length + i;
        return (
          <g key={`f${i}`}>
            <circle cx={xOf(idx)} cy={yOf(p.val)} r="4" fill={C.azure} />
            <text x={xOf(idx) + 8} y={yOf(p.val) - 4} fontSize="11" fill={C.azure} fontWeight="600">
              {p.kind === "fc6" ? "6m" : "12m"}: {(p.val * 100).toFixed(2)}%
            </text>
          </g>
        );
      })}

      {/* Last hist label */}
      {histPts.length > 0 && (() => {
        const last = histPts[histPts.length - 1];
        return (
          <text x={xOf(histPts.length - 1) + 6} y={yOf(last.val) + 14} fontSize="10" fill={C.text} fontWeight="600">
            atual: {(last.val * 100).toFixed(2)}%
          </text>
        );
      })()}

      {/* X-axis labels (sparse) */}
      {allPts.filter((_, i) => i === 0 || i === histPts.length - 1 || i === allPts.length - 1).map((p, j) => {
        const idx = allPts.indexOf(p);
        return (
          <text key={j} x={xOf(idx)} y={h - PAD.b + 16} fontSize="10" fill={C.dim} textAnchor="middle">
            {p.data.slice(0, 7)}
          </text>
        );
      })}

      {/* Vertical line: today */}
      {histPts.length > 0 && (
        <line
          x1={xOf(histPts.length - 1)} y1={PAD.t}
          x2={xOf(histPts.length - 1)} y2={PAD.t + cH}
          stroke={C.dim} strokeDasharray="3,3"
        />
      )}
      {histPts.length > 0 && (
        <text x={xOf(histPts.length - 1)} y={PAD.t - 6} fontSize="9" fill={C.dim} textAnchor="middle">HOJE</text>
      )}
    </svg>
  );
}

export default function BenchmarkPanel({ initialCnae, initialUf, initialModalidade = "Empréstimos", pdIndividual = null }) {
  // Auto-detect from initialCnae
  const initialSection = cnaeToSection(initialCnae);

  const [section, setSection] = useState(initialSection || "");
  const [uf, setUf] = useState(initialUf || "SP");
  const [modalidade, setModalidade] = useState(initialModalidade);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cnae2 = section ? `S${section}` : null;
  const sectionInfo = SECTIONS.find(s => s.letter === section);

  useEffect(() => {
    if (!cnae2 || !uf) { setData(null); setError(null); return; }
    setLoading(true);
    setError(null);
    const pdParam = pdIndividual != null ? `?pd_individual=${pdIndividual / 100}` : "";
    const url = `${PRS_BASE}/benchmark/${cnae2}/${uf}/${encodeURIComponent(modalidade)}${pdParam}`;
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(d => {
        if (d.error) setError(d.error); else setData(d);
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [cnae2, uf, modalidade, pdIndividual]);

  const inadSetor = data?.current?.inadimplencia_atual;
  const score = data?.score_benchmark_setorial?.score;
  const deltaPp = data?.score_benchmark_setorial?.delta_pp;
  const align = data?.score_benchmark_setorial?.alinhamento;
  const alignCol = alignColor(align);
  const fc6 = data?.forecast?.["6m"];
  const fc12 = data?.forecast?.["12m"];
  const nat6 = data?.national_trend?.["6m"];
  const nat12 = data?.national_trend?.["12m"];

  return (
    <div style={{ padding: "0 4px" }}>
      {/* Header com seletores */}
      <div style={{
        marginBottom: 18, padding: 14,
        background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
          📈 Benchmark Setorial — SCR.data Bacen
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
          Comparação da inadimplência da empresa vs setor de atuação. Forecasts via XGBoost (cell-level) + BSTS (national overlay).
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: 12 }}>
          <div>
            <label style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Setor (CNAE Section)
            </label>
            <select
              value={section}
              onChange={e => setSection(e.target.value)}
              style={{
                width: "100%", padding: "6px 8px", marginTop: 4,
                fontSize: 12, border: `1px solid ${C.border}`, borderRadius: 6,
              }}
            >
              <option value="">Selecionar setor...</option>
              {SECTIONS.map(s => (
                <option key={s.letter} value={s.letter}>
                  {s.letter} · {s.label} (CNAE {s.range})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              UF
            </label>
            <select
              value={uf}
              onChange={e => setUf(e.target.value)}
              style={{
                width: "100%", padding: "6px 8px", marginTop: 4,
                fontSize: 12, border: `1px solid ${C.border}`, borderRadius: 6,
              }}
            >
              <option value="">UF...</option>
              {UFS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Modalidade de crédito
            </label>
            <select
              value={modalidade}
              onChange={e => setModalidade(e.target.value)}
              style={{
                width: "100%", padding: "6px 8px", marginTop: 4,
                fontSize: 12, border: `1px solid ${C.border}`, borderRadius: 6,
              }}
            >
              {MODALIDADES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {!initialCnae && (
          <div style={{
            marginTop: 10, padding: "6px 10px", fontSize: 11, color: C.amber,
            background: `${C.amber}10`, borderRadius: 4, border: `1px solid ${C.amber}30`,
          }}>
            ⚠ CNAE da empresa não detectado no cadastro. Selecione manualmente acima ou peça pro tomador subir o cartão CNPJ.
          </div>
        )}
      </div>

      {/* Estado: sem seleção */}
      {(!cnae2 || !uf) && (
        <div style={{
          padding: 40, textAlign: "center", background: C.bg,
          border: `1px dashed ${C.border}`, borderRadius: 10, color: C.muted,
        }}>
          Selecione setor e UF acima para ver o benchmark.
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
          ⏳ Carregando benchmark...
        </div>
      )}

      {/* Erro */}
      {error && !loading && (
        <div style={{
          padding: 20, background: `${C.red}08`, borderRadius: 8,
          border: `1px solid ${C.red}30`, color: C.red, fontSize: 12,
        }}>
          {error === "no_data_for_cnae_uf_modalidade"
            ? `Sem dados pra ${cnae2} × ${uf} × ${modalidade}. Tente outra combinação.`
            : `Erro: ${error}`}
        </div>
      )}

      {/* Dados */}
      {data && !loading && !error && (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          {/* COLUNA ESQUERDA: chart grande */}
          <div style={{
            padding: 14, background: C.card, borderRadius: 10,
            border: `1px solid ${C.border}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  Inadimplência setor
                </div>
                <div style={{ fontSize: 10, color: C.dim }}>
                  {sectionInfo?.label} · {uf} · {modalidade}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                  {fPct(inadSetor, 2)}
                </div>
                <div style={{ fontSize: 10, color: C.dim }}>data-base {data.current?.data_base}</div>
              </div>
            </div>
            <ChartTendenciaForecast trend={data.trend_12m} fc6={fc6} fc12={fc12} currentInad={inadSetor} />
            <div style={{ display: "flex", gap: 16, justifyContent: "center", fontSize: 10, color: C.dim, marginTop: 6 }}>
              <span>━ histórico (12m)</span>
              <span>┄┄ forecast (XGBoost)</span>
              <span>▭ banda IC 80%/95%</span>
            </div>
          </div>

          {/* COLUNA DIREITA: scoring + diagnostics */}
          <div style={{ display: "grid", gap: 14 }}>
            {/* Score card */}
            <div style={{
              padding: 14, background: `${alignCol}08`, borderRadius: 10,
              border: `1px solid ${alignCol}30`,
            }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                Score Benchmark Setorial
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: alignCol, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.1, marginTop: 4 }}>
                {score != null ? Math.round(score) : "—"}
                <span style={{ fontSize: 12, color: C.dim, fontWeight: 400 }}>/100</span>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: alignCol, marginTop: 6, letterSpacing: 0.3 }}>
                {alignLabel(align)}
              </div>
              {pdIndividual != null && deltaPp != null && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: C.bg, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: C.muted }}>PD individual: <strong>{pdIndividual.toFixed(2)}%</strong></div>
                  <div style={{ fontSize: 10, color: C.muted }}>Setor: <strong>{fPct(inadSetor, 2)}</strong></div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: alignCol, marginTop: 4 }}>
                    Δ {deltaPp >= 0 ? "+" : ""}{deltaPp.toFixed(2)}pp vs setor
                  </div>
                </div>
              )}
            </div>

            {/* National trend overlay */}
            {nat6 && (
              <div style={{
                padding: 12, background: C.card, borderRadius: 10, border: `1px solid ${C.border}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                  🌐 Tendência Nacional Setor (BSTS)
                </div>
                <div style={{ fontSize: 10, color: C.dim, marginBottom: 8 }}>
                  Agregado nacional do setor — suaviza variações regionais.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 9, color: C.muted }}>6 meses</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                      {fPct(nat6.point, 2)}
                    </div>
                    <div style={{ fontSize: 9, color: C.dim }}>
                      IC80: {fPct(nat6.ic_80[0], 1)}–{fPct(nat6.ic_80[1], 1)}
                    </div>
                  </div>
                  {nat12 && (
                    <div>
                      <div style={{ fontSize: 9, color: C.muted }}>12 meses</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                        {fPct(nat12.point, 2)}
                      </div>
                      <div style={{ fontSize: 9, color: C.dim }}>
                        IC80: {fPct(nat12.ic_80[0], 1)}–{fPct(nat12.ic_80[1], 1)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Setor stats */}
            <div style={{
              padding: 12, background: C.card, borderRadius: 10, border: `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                📊 Volume do setor
              </div>
              <div style={{ display: "grid", gap: 6, fontSize: 11 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: C.muted }}>Carteira ativa</span>
                  <span style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    {data.current?.carteira_ativa_total
                      ? `R$ ${(data.current.carteira_ativa_total / 1e9).toFixed(1)}B`
                      : "—"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: C.muted }}>Operações</span>
                  <span style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    {data.current?.n_operacoes?.toLocaleString("pt-BR") || "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Diagnostics */}
            <div style={{
              padding: 10, background: C.bg, borderRadius: 8, fontSize: 10, color: C.dim,
            }}>
              <div>Modelo principal: <strong>{fc6?.modelo || "—"}</strong></div>
              {fc6?.rmse_backtest && <div>RMSE backtest: <strong>{(fc6.rmse_backtest * 100).toFixed(1)}pp</strong></div>}
              {fc6?.data_ref && <div>Última calibração: <strong>{fc6.data_ref}</strong></div>}
              {data.fallback_strategy && <div style={{ color: C.amber }}>⚠ Fallback: {data.fallback_strategy}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
